import { useState, useEffect, useCallback } from "react";
import { Loader2, ShoppingBag, Check } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { toast } from "sonner";
import { useFb } from "../../utils/football";
import {
  initializeRevenueCat,
  getOfferings,
  purchaseProduct,
  isNativePlatform,
} from "../../utils/revenuecat";

// ---- Custom on-theme SVG icons (no licensed imagery) ----
const SubBoardIcon = ({ label = "12" }) => (
  <svg viewBox="0 0 64 64" className="h-14 w-14" aria-hidden>
    <rect x="6" y="14" width="52" height="30" rx="4" fill="#0b1437" stroke="var(--wc-gold,#e9c46a)" strokeWidth="2.5" />
    <rect x="11" y="19" width="42" height="20" rx="2" fill="#04210f" />
    <text x="32" y="35" textAnchor="middle" fontSize="16" fontWeight="800" fill="#22e06a" fontFamily="monospace">{label}</text>
    <rect x="28" y="44" width="8" height="12" fill="var(--wc-gold,#e9c46a)" />
    <rect x="22" y="56" width="20" height="4" rx="2" fill="var(--wc-gold,#e9c46a)" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 64 64" className="h-14 w-14" aria-hidden>
    <path d="M32 6 L54 14 V32 C54 46 44 54 32 58 C20 54 10 46 10 32 V14 Z" fill="#0b1437" stroke="var(--wc-gold,#e9c46a)" strokeWidth="2.5" />
    <path d="M22 30 q10 -10 20 0" fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
    <circle cx="26" cy="36" r="3.5" fill="#fff" />
    <circle cx="38" cy="36" r="3.5" fill="#fff" />
    <path d="M16 16 L48 48" stroke="#ef4444" strokeWidth="3.5" strokeLinecap="round" />
  </svg>
);

const PRODUCTS = [
  {
    id: "apl.football.super_sub.1",
    invKey: "super_sub",
    name: "Super Sub",
    desc: "Bring on a 12th player off the bench. Scores at 1x, no captain eligibility.",
    fallbackPrice: "AED 10.99",
    icon: <SubBoardIcon label="12" />,
  },
  {
    id: "apl.football.super_sub.5pack",
    invKey: "super_sub",
    name: "Super Sub Pack",
    desc: "Stock the bench with five Super Subs. Save 36%.",
    fallbackPrice: "AED 34.99",
    ribbon: "SAVE 36%",
    icon: <SubBoardIcon label="×5" />,
  },
  {
    id: "apl.football.penalty_shield.1",
    invKey: "no_penalty_shield",
    name: "No Penalty Shield",
    desc: "Avoid the heartbreak — zero out red cards, own goals, and missed penalties for one match day.",
    fallbackPrice: "AED 14.99",
    icon: <ShieldIcon />,
  },
];

export default function FootballShopView({ user }) {
  const fb = useFb();
  const [inventory, setInventory] = useState({ super_sub: 0, no_penalty_shield: 0, captain_boost: 0 });
  const [prices, setPrices] = useState({});
  const [isNative] = useState(isNativePlatform());
  const [rcReady, setRcReady] = useState(false);
  const [storeUnavailable, setStoreUnavailable] = useState(false);
  const [buying, setBuying] = useState("");
  const [loading, setLoading] = useState(true);

  const loadInventory = useCallback(async () => {
    try {
      const inv = await fb.get("/football/consumables/inventory");
      setInventory(inv || {});
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadInventory();
      if (isNative && user?.uid) {
        try {
          const ok = await initializeRevenueCat(user.uid);
          setRcReady(ok);
          const offerings = await getOfferings();
          const pkgs = offerings?.current?.availablePackages || [];
          if (!pkgs.length) {
            setStoreUnavailable(true);
          } else {
            const pm = {};
            pkgs.forEach((pk) => { if (pk.product?.identifier) pm[pk.product.identifier] = pk.product.priceString; });
            setPrices(pm);
          }
        } catch {
          setStoreUnavailable(true);
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNative, user?.uid]);

  const buy = async (p) => {
    if (!isNative) {
      toast.info("Power-Ups are available in the APL mobile app.");
      return;
    }
    if (!rcReady || storeUnavailable) {
      toast.error("Store temporarily unavailable. Please try again later.");
      return;
    }
    setBuying(p.id);
    try {
      const res = await purchaseProduct(p.id);
      if (res?.cancelled) { setBuying(""); return; }
      if (res?.success) {
        try {
          await fb.post("/football/consumables/purchase", {
            product_id: p.id,
            transaction_id: res.transactionIdentifier,
            platform: Capacitor.getPlatform(),
          });
        } catch { /* webhook is source of truth */ }
        await loadInventory();
        toast.success(`${p.name} added to your inventory!`);
      } else {
        toast.error("Purchase could not be completed.");
      }
    } catch (e) {
      toast.error(e?.message || "Purchase failed.");
    }
    setBuying("");
  };

  const buyDisabled = (p) => buying === p.id || (isNative && (!rcReady || storeUnavailable));

  return (
    <div className="football-theme mx-auto max-w-3xl px-4 pb-16" data-testid="football-shop-view">
      {/* Header */}
      <div className="wc-hero-gradient -mx-4 mb-6 px-4 pb-7 pt-6 text-center">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-1.5">
          <ShoppingBag className="h-4 w-4 wc-gold" />
          <span className="text-xs font-bold uppercase tracking-wider wc-gold">Shop</span>
        </div>
        <h1 className="text-3xl font-extrabold text-white sm:text-4xl">Power-Ups</h1>
        <p className="mt-1 text-base text-white/70">Match Day Boosts</p>
      </div>

      {storeUnavailable && (
        <div className="mb-4 rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-center text-sm text-amber-200" data-testid="shop-unavailable-banner">
          Store temporarily unavailable. Power-Ups can't be purchased right now — please try again later.
        </div>
      )}

      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="animate-spin text-amber-300" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {PRODUCTS.map((p) => {
            const owned = inventory?.[p.invKey] ?? 0;
            const price = prices[p.id] || p.fallbackPrice;
            return (
              <div
                key={p.id}
                className="relative overflow-hidden rounded-2xl border border-amber-400/30 bg-card p-4"
                style={{ background: "radial-gradient(120% 90% at 0% 0%, rgba(220,38,38,0.10), transparent 55%), radial-gradient(120% 90% at 100% 100%, rgba(37,99,235,0.12), transparent 55%), var(--card)" }}
                data-testid={`shop-card-${p.invKey}-${p.id.includes('5pack') ? 'pack' : '1'}`}
              >
                {p.ribbon && (
                  <div className="absolute right-3 top-3 rounded-full bg-amber-400 px-2.5 py-0.5 text-[10px] font-extrabold text-slate-900" data-testid="shop-ribbon">
                    {p.ribbon}
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="shrink-0">{p.icon}</div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-extrabold text-foreground">{p.name}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{p.desc}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-bold text-foreground" data-testid={`shop-owned-${p.invKey}-${p.id.includes('5pack') ? 'pack' : '1'}`}>
                    <Check size={11} className="text-emerald-400" /> You own: {owned}
                  </span>
                  <span className="text-sm font-extrabold wc-gold">{price}</span>
                </div>
                <button
                  onClick={() => buy(p)}
                  disabled={buyDisabled(p)}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl wc-bg-gold py-3 text-sm font-extrabold text-slate-900 transition-opacity disabled:opacity-40"
                  data-testid={`shop-buy-${p.id}`}
                >
                  {buying === p.id ? <Loader2 size={16} className="animate-spin" />
                    : !isNative ? "Available in App"
                    : storeUnavailable ? "Temporarily Unavailable"
                    : `Buy · ${price}`}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-6 text-center text-xs text-muted-foreground">
        Power-Ups never expire and roll over across tournaments. Use them on your locked squad before kickoff.
      </p>
    </div>
  );
}
